import { z } from "zod";

export const mediaSchema = z.object({
  url: z.string().url().or(z.literal("")),
  type: z.enum(["image", "video"]).default("image"),
});
export type Media = z.infer<typeof mediaSchema>;

export const heroSchema = z.object({
  kicker: z.string().max(200).optional().default(""),
  title: z.string().max(300).optional().default(""),
  subtitle: z.string().max(500).optional().default(""),
  media: mediaSchema.optional(),
  alignment: z.enum(["left", "center"]).optional().default("left"),
});

export const mediaTextSplitSchema = z.object({
  media: mediaSchema.optional(),
  side: z.enum(["left", "right"]).optional().default("left"),
  kicker: z.string().max(200).optional().default(""),
  title: z.string().max(300).optional().default(""),
  body: z.string().max(3000).optional().default(""),
  cta_label: z.string().max(60).optional().default(""),
  cta_url: z.string().max(2000).optional().default(""),
});

export const fullBleedMediaSchema = z.object({
  media: mediaSchema.optional(),
  kicker: z.string().max(200).optional().default(""),
  title: z.string().max(300).optional().default(""),
  caption: z.string().max(1000).optional().default(""),
  text_position: z.enum(["bottom", "center", "top"]).optional().default("bottom"),
});

export const galleryItemSchema = z.object({
  media: mediaSchema,
  caption: z.string().max(400).optional().default(""),
});

export const gallerySchema = z.object({
  layout: z.enum(["grid", "masonry", "strip"]).optional().default("grid"),
  items: z.array(galleryItemSchema).optional().default([]),
});

export const pullQuoteSchema = z.object({
  quote: z.string().max(800).optional().default(""),
  attribution: z.string().max(200).optional().default(""),
  background_media: mediaSchema.optional(),
});

export const statItemSchema = z.object({
  label: z.string().max(120).optional().default(""),
  value: z.string().max(40).optional().default(""),
  suffix: z.string().max(20).optional().default(""),
});

export const statsSchema = z.object({
  items: z.array(statItemSchema).optional().default([]),
});

export const ctaSchema = z.object({
  kicker: z.string().max(200).optional().default(""),
  title: z.string().max(300).optional().default(""),
  body: z.string().max(1000).optional().default(""),
  button_label: z.string().max(60).optional().default(""),
  button_url: z.string().max(2000).optional().default(""),
});

export const accordionItemSchema = z.object({
  question: z.string().max(300).optional().default(""),
  answer: z.string().max(2000).optional().default(""),
});

export const accordionInlineSchema = z.object({
  title: z.string().max(200).optional().default(""),
  items: z.array(accordionItemSchema).optional().default([]),
});

export const SCHEMAS = {
  hero: heroSchema,
  media_text_split: mediaTextSplitSchema,
  full_bleed_media: fullBleedMediaSchema,
  gallery: gallerySchema,
  pull_quote: pullQuoteSchema,
  stats: statsSchema,
  cta: ctaSchema,
  accordion_inline: accordionInlineSchema,
} as const;

export type SectionTypeKey = keyof typeof SCHEMAS;

export type HeroData = z.infer<typeof heroSchema>;
export type MediaTextSplitData = z.infer<typeof mediaTextSplitSchema>;
export type FullBleedMediaData = z.infer<typeof fullBleedMediaSchema>;
export type GalleryData = z.infer<typeof gallerySchema>;
export type PullQuoteData = z.infer<typeof pullQuoteSchema>;
export type StatsData = z.infer<typeof statsSchema>;
export type CtaData = z.infer<typeof ctaSchema>;
export type AccordionInlineData = z.infer<typeof accordionInlineSchema>;

export const SECTION_TYPE_LABELS: Record<SectionTypeKey, string> = {
  hero: "Hero",
  media_text_split: "Media + Text",
  full_bleed_media: "Full-bleed media",
  gallery: "Gallery",
  pull_quote: "Pull quote",
  stats: "Stats",
  cta: "Call to action",
  accordion_inline: "Accordion (inline)",
};

export function safeParseSection<T extends SectionTypeKey>(
  type: T,
  raw: unknown,
): z.infer<(typeof SCHEMAS)[T]> {
  const schema = SCHEMAS[type] as z.ZodTypeAny;
  const r = schema.safeParse(raw ?? {});
  if (r.success) return r.data;
  return schema.parse({});
}
